<script setup lang="ts">
import { ref, onMounted, onUnmounted, watch } from 'vue';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { kdm, judgmentOptions, computeCellWinner } from '@/store/kdm';

const props = defineProps<{
  selectedAxis: 'x' | 'y' | 'z'; // Which axis is selected for slicing
  selectedIndex: number; // Index on the selected axis
  showAxes?: boolean;
  showFullLabels?: boolean;
  showLabels?: boolean; // Toggle to show/hide axis labels
  isFullscreen?: boolean;
  visibleEvaluationValues?: Set<string>; // Filter by evaluation values
  showAllOpaque?: boolean; // Toggle to show all slices at full opacity
  cellDetailMode?: boolean; // Enable cell clicking to show details
  selectedCellId?: string | null; // Currently selected cell for highlighting
}>();

const emit = defineEmits<{
  (e: 'select-slice', axis: 'x' | 'y' | 'z', index: number): void;
  (e: 'cell-click', cellId: string, stakeholderIndex: number, rowIndex: number, colIndex: number): void;
}>();

const containerRef = ref<HTMLDivElement | null>(null);
const hoveredCell = ref<THREE.Mesh | null>(null);

let scene: THREE.Scene;
let resizeObserver: ResizeObserver | null = null;
let camera: THREE.PerspectiveCamera;
let renderer: THREE.WebGLRenderer;
let controls: OrbitControls;
let cubeGroup: THREE.Group;
let labelsGroup: THREE.Group;
let animationId: number;
let raycaster: THREE.Raycaster;
let mouse: THREE.Vector2;
let axesGroup: THREE.Group | null = null;

// Cell size and spacing
const CELL_SIZE = 1;
const CELL_GAP = 0.1;
const LAYER_GAP = 1.5;

// Create text sprite for labels - sized to fit text content
function createTextSprite(text: string, options: {
  fontSize?: number;
  color?: string;
  backgroundColor?: string;
  maxWidth?: number;
  fullText?: boolean;
  textAlign?: 'left' | 'center' | 'right';
} = {}): THREE.Sprite {
  const {
    fontSize = 48,
    color = '#333333',
    backgroundColor = 'transparent',
    maxWidth = 200,
    fullText = false,
  } = options;

  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d')!;

  // Measure text first to determine canvas size
  context.font = `bold ${fontSize}px Arial, sans-serif`;

  // Truncate text if too long (unless fullText is true)
  let displayText = text;
  if (!fullText) {
    const metrics = context.measureText(displayText);
    if (metrics.width > maxWidth) {
      while (context.measureText(displayText + '...').width > maxWidth && displayText.length > 3) {
        displayText = displayText.slice(0, -1);
      }
      displayText += '...';
    }
  }

  // Calculate actual text width and add padding
  const textMetrics = context.measureText(displayText);
  const padding = 20;
  const textWidth = textMetrics.width + padding * 2;

  // Set canvas size to fit text
  canvas.width = Math.max(64, Math.ceil(textWidth));
  canvas.height = 64;

  // Re-set font after canvas resize (canvas resize clears context state)
  context.font = `bold ${fontSize}px Arial, sans-serif`;
  context.textBaseline = 'middle';

  // Background
  if (backgroundColor !== 'transparent') {
    context.fillStyle = backgroundColor;
    context.fillRect(0, 0, canvas.width, canvas.height);
  }

  // Draw text centered in the canvas
  context.fillStyle = color;
  context.textAlign = 'center';
  context.fillText(displayText, canvas.width / 2, canvas.height / 2);

  // Create sprite
  const texture = new THREE.CanvasTexture(canvas);
  texture.needsUpdate = true;

  const material = new THREE.SpriteMaterial({
    map: texture,
    transparent: true,
  });

  const sprite = new THREE.Sprite(material);

  // Scale sprite proportionally to text width (in 3D units)
  const scaleX = canvas.width / 128; // Base unit conversion
  const scaleY = 0.5;
  sprite.scale.set(scaleX, scaleY, 1);

  // Store text width for positioning calculations
  sprite.userData.textWidth = scaleX;

  return sprite;
}

// Create rotatable text mesh (for angled labels like X-axis) - sized to fit text content
function createTextMesh(text: string, options: {
  fontSize?: number;
  color?: string;
  maxWidth?: number;
  fullText?: boolean;
} = {}): THREE.Mesh {
  const {
    fontSize = 48,
    color = '#333333',
    maxWidth = 200,
    fullText = false,
  } = options;

  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d')!;

  // Measure text first to determine canvas size
  context.font = `bold ${fontSize}px Arial, sans-serif`;

  let displayText = text;
  if (!fullText) {
    const metrics = context.measureText(displayText);
    if (metrics.width > maxWidth) {
      while (context.measureText(displayText + '...').width > maxWidth && displayText.length > 3) {
        displayText = displayText.slice(0, -1);
      }
      displayText += '...';
    }
  }

  // Calculate actual text width and add padding
  const textMetrics = context.measureText(displayText);
  const padding = 20;
  const textWidth = textMetrics.width + padding * 2;

  // Set canvas size to fit text
  canvas.width = Math.max(64, Math.ceil(textWidth));
  canvas.height = 64;

  // Re-set font after canvas resize
  context.font = `bold ${fontSize}px Arial, sans-serif`;
  context.textBaseline = 'middle';
  context.fillStyle = color;
  context.textAlign = 'center';
  context.fillText(displayText, canvas.width / 2, canvas.height / 2);

  const texture = new THREE.CanvasTexture(canvas);
  texture.needsUpdate = true;

  // Scale geometry proportionally to text width
  const scaleX = canvas.width / 128;
  const scaleY = 0.5;
  const geometry = new THREE.PlaneGeometry(scaleX, scaleY);
  const material = new THREE.MeshBasicMaterial({
    map: texture,
    transparent: true,
    side: THREE.DoubleSide
  });

  const mesh = new THREE.Mesh(geometry, material);
  mesh.userData.textWidth = scaleX;

  return mesh;
}

// Get color for a cell based on its evaluation value - uses shared algorithm
function getCellColor(stakeholderIndex: number, rowIndex: number, colIndex: number): { color: string; judgmentId: string | null } {
  const stakeholder = kdm.value?.stake_holders?.[stakeholderIndex];
  if (!stakeholder) return { color: '#ffffff', judgmentId: null };

  const cellId = `${stakeholder.id}-${kdm.value?.rows?.[rowIndex]?.id}-${kdm.value?.columns?.[colIndex]?.id}`;

  let matrixData: Record<string, any> = {};
  if (kdm.value?.field_matrix_data) {
    try {
      matrixData = typeof kdm.value.field_matrix_data === 'string'
        ? JSON.parse(kdm.value.field_matrix_data)
        : kdm.value.field_matrix_data;
    } catch (e) {
      return { color: '#ffffff', judgmentId: null };
    }
  }

  const cellData = matrixData[cellId];
  if (!cellData) return { color: '#ffffff', judgmentId: null };

  // Use shared algorithm for cell winner calculation
  const winner = computeCellWinner(cellData, kdm.value?.kdm_type);
  if (!winner) return { color: '#ffffff', judgmentId: null };

  return { color: winner.color, judgmentId: winner.judgmentId };
}

function createCube() {
  if (!kdm.value) return;

  // Clear existing groups
  if (cubeGroup) scene.remove(cubeGroup);
  if (labelsGroup) scene.remove(labelsGroup);

  cubeGroup = new THREE.Group();
  labelsGroup = new THREE.Group();

  const rows = kdm.value.rows || [];
  const cols = kdm.value.columns || [];
  const stakeholders = kdm.value.stake_holders || [];

  const visibleEvals = props.visibleEvaluationValues;
  const useFullText = props.showFullLabels ?? false;
  const labelOffset = useFullText ? 2.5 : 1.2;

  // Fixed cube structure: X=columns, Y=rows, Z=stakeholders
  const totalWidth = cols.length * (CELL_SIZE + CELL_GAP);
  const totalHeight = rows.length * (CELL_SIZE + CELL_GAP);
  const totalDepth = stakeholders.length * (CELL_SIZE + LAYER_GAP);

  // Create all cells - structure never changes
  stakeholders.forEach((stakeholder, zIndex) => {
    const layerGroup = new THREE.Group();
    layerGroup.userData = { zIndex, type: 'layer' };

    rows.forEach((row, yIndex) => {
      cols.forEach((col, xIndex) => {
        const cellInfo = getCellColor(zIndex, yIndex, xIndex);

        // Apply evaluation value filter
        const isFiltered = visibleEvals && visibleEvals.size > 0 &&
          cellInfo.judgmentId && !visibleEvals.has(cellInfo.judgmentId);

        // Determine if this cell is in the selected slice
        const isInSelectedSlice = isCellInSelectedSlice(xIndex, yIndex, zIndex);

        // Cell is "empty" if no judgmentId
        const isEmpty = !cellInfo.judgmentId;

        const geometry = new THREE.BoxGeometry(CELL_SIZE, CELL_SIZE, CELL_SIZE * 0.3);

        // Empty cells in active slice: transparent with wireframe
        // Filled cells: show the judgment color
        let cellOpacity: number;
        let cellColor: string;

        if (isFiltered) {
          cellColor = '#ffffff';
          cellOpacity = 0.02;
        } else if (isEmpty) {
          cellColor = '#ffffff';
          cellOpacity = isInSelectedSlice ? 0.1 : 0.05; // Very transparent for empty cells
        } else {
          cellColor = cellInfo.color;
          // Show all opaque if toggle is on, otherwise use gradient
          cellOpacity = props.showAllOpaque ? 1 : (isInSelectedSlice ? 1 : 0.3);
        }

        const material = new THREE.MeshBasicMaterial({
          color: cellColor,
          transparent: true,
          opacity: cellOpacity,
        });

        const cell = new THREE.Mesh(geometry, material);
        const cellX = xIndex * (CELL_SIZE + CELL_GAP) - totalWidth / 2 + CELL_SIZE / 2;
        const cellY = -yIndex * (CELL_SIZE + CELL_GAP) + totalHeight / 2 - CELL_SIZE / 2;
        const cellZ = -zIndex * (CELL_SIZE + LAYER_GAP);

        cell.position.set(cellX, cellY, cellZ);

        cell.userData = {
          xIndex, // column
          yIndex, // row
          zIndex, // stakeholder
          type: 'cell',
          isEmpty
        };

        layerGroup.add(cell);

        // Add wireframe for cells in the active slice
        if (isInSelectedSlice) {
          const edges = new THREE.EdgesGeometry(geometry);
          const wireframeMaterial = new THREE.LineBasicMaterial({
            color: isEmpty ? '#8899aa' : '#667788', // Blue-gray wireframe
            linewidth: 1
          });
          const wireframe = new THREE.LineSegments(edges, wireframeMaterial);
          wireframe.position.set(cellX, cellY, cellZ);
          wireframe.userData = { type: 'wireframe' };
          layerGroup.add(wireframe);
        }
      });
    });

    cubeGroup.add(layerGroup);
  });

  // === AXIS LABELS (always in fixed positions) ===
  // Labels are sized to fit their text content for precise clicking

  // X-axis labels (Columns) - BELOW the cube, rotated 90° around Y axis
  cols.forEach((col, xIndex) => {
    const isSelected = props.selectedAxis === 'x' && xIndex === props.selectedIndex;
    const label = createTextMesh(col.title, {
      fontSize: isSelected ? 48 : 36,
      color: isSelected ? '#990000' : '#cc4444',
      fullText: useFullText,
    });
    const labelWidth = label.userData.textWidth || 1;
    // Position: centered on column, offset by half label width so it extends from cube edge
    label.position.x = xIndex * (CELL_SIZE + CELL_GAP) - totalWidth / 2 + CELL_SIZE / 2;
    label.position.y = -totalHeight / 2 - 0.5;
    label.position.z = 0.3 + labelWidth / 2;
    label.rotation.y = Math.PI / 2;
    label.userData = { ...label.userData, axis: 'x', index: xIndex, type: 'axis-label' };
    if (isSelected) {
      label.scale.set(1.2, 1.2, 1);
    }
    labelsGroup.add(label);
  });

  // Y-axis labels (Rows) - LEFT side, text right-aligned to cube edge
  rows.forEach((row, yIndex) => {
    const isSelected = props.selectedAxis === 'y' && yIndex === props.selectedIndex;
    const label = createTextSprite(row.title, {
      fontSize: isSelected ? 48 : 36,
      color: isSelected ? '#0f5132' : '#198754',
      fullText: useFullText,
    });
    const labelWidth = label.userData.textWidth || 1;
    // Position: offset by half label width so right edge aligns near cube edge
    label.position.x = -totalWidth / 2 - 0.3 - labelWidth / 2;
    label.position.y = -yIndex * (CELL_SIZE + CELL_GAP) + totalHeight / 2 - CELL_SIZE / 2;
    label.position.z = 1.0;
    label.userData = { ...label.userData, axis: 'y', index: yIndex, type: 'axis-label' };
    labelsGroup.add(label);
  });

  // Z-axis labels (Stakeholders) - Along Z axis, with background
  stakeholders.forEach((sh, zIndex) => {
    const isSelected = props.selectedAxis === 'z' && zIndex === props.selectedIndex;
    const label = createTextSprite(sh.title, {
      fontSize: isSelected ? 48 : 36,
      color: isSelected ? '#000099' : '#4466cc',
      backgroundColor: isSelected ? '#cce0ff' : '#e8f0ff',
      fullText: useFullText,
    });
    const labelWidth = label.userData.textWidth || 1;
    // Position: offset by half label width so right edge aligns near cube edge
    label.position.x = -totalWidth / 2 - 0.3 - labelWidth / 2;
    label.position.y = -totalHeight / 2 - 0.5;
    label.position.z = -zIndex * (CELL_SIZE + LAYER_GAP);
    label.userData = { ...label.userData, axis: 'z', index: zIndex, type: 'axis-label' };
    labelsGroup.add(label);
  });

  // Center groups
  cubeGroup.position.z = totalDepth / 2;
  labelsGroup.position.z = totalDepth / 2;

  scene.add(cubeGroup);
  scene.add(labelsGroup);

  // Apply initial labels visibility
  labelsGroup.visible = props.showLabels ?? true;
}

// Check if a cell is in the currently selected slice
function isCellInSelectedSlice(xIndex: number, yIndex: number, zIndex: number): boolean {
  const { selectedAxis, selectedIndex } = props;
  if (selectedAxis === 'x') return xIndex === selectedIndex;
  if (selectedAxis === 'y') return yIndex === selectedIndex;
  return zIndex === selectedIndex; // default 'z'
}

function updateLayerOpacity() {
  if (!cubeGroup) return;

  const visibleEvals = props.visibleEvaluationValues;

  // Update all cells and wireframes based on whether they're in the selected slice
  cubeGroup.children.forEach((layerGroup) => {
    layerGroup.children.forEach((child) => {
      if (child instanceof THREE.Mesh && child.userData.type === 'cell') {
        const { xIndex, yIndex, zIndex, isEmpty } = child.userData;
        const isInSelectedSlice = isCellInSelectedSlice(xIndex, yIndex, zIndex);

        // Also check evaluation filter
        const cellInfo = getCellColor(zIndex, yIndex, xIndex);
        const isFiltered = visibleEvals && visibleEvals.size > 0 &&
          cellInfo.judgmentId && !visibleEvals.has(cellInfo.judgmentId);

        let opacity: number;
        if (isFiltered) {
          opacity = 0.02;
        } else if (isEmpty) {
          opacity = isInSelectedSlice ? 0.1 : 0.05;
        } else {
          opacity = props.showAllOpaque ? 1 : (isInSelectedSlice ? 1 : 0.3);
        }

        (child.material as THREE.MeshBasicMaterial).opacity = opacity;
      }

      // Show/hide wireframes based on active slice
      if (child instanceof THREE.LineSegments && child.userData.type === 'wireframe') {
        child.visible = false; // Wireframes are created per-slice in createCube, so we rebuild
      }
    });
  });

  // Update axis labels to show which one is selected
  if (labelsGroup) {
    const { selectedAxis, selectedIndex } = props;

    labelsGroup.children.forEach((child) => {
      if (child.userData.type === 'axis-label') {
        const isSelected = child.userData.axis === selectedAxis && child.userData.index === selectedIndex;
        const labelAxis = child.userData.axis;
        const textWidth = child.userData.textWidth || 1;

        if (labelAxis === 'x') {
          // X labels are Meshes - scale uniformly
          child.scale.set(isSelected ? 1.2 : 1, isSelected ? 1.2 : 1, 1);
        } else {
          // Y and Z labels are Sprites - scale based on their text width
          child.scale.set(isSelected ? textWidth * 1.2 : textWidth, isSelected ? 0.6 : 0.5, 1);
        }
      }
    });
  }
}

function onMouseClick(event: MouseEvent) {
  if (!containerRef.value) return;

  const rect = containerRef.value.getBoundingClientRect();
  mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
  mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

  raycaster.setFromCamera(mouse, camera);

  // Check axis labels first (higher priority)
  const labelIntersects = raycaster.intersectObjects(labelsGroup.children);
  if (labelIntersects.length > 0) {
    const clickedLabel = labelIntersects[0].object;
    if (clickedLabel.userData.type === 'axis-label') {
      const { axis, index } = clickedLabel.userData;
      emit('select-slice', axis, index);
      return;
    }
  }

  // Check cells - when clicking a cell, use the current selectedAxis to determine which slice to select
  const cellIntersects = raycaster.intersectObjects(cubeGroup.children, true);

  // Find the first cell object (skip wireframes and other non-cell objects)
  for (const intersect of cellIntersects) {
    const clickedObject = intersect.object;
    if (clickedObject.userData.type === 'cell') {
      const { xIndex, yIndex, zIndex } = clickedObject.userData;
      const { selectedAxis } = props;

      // Check if this cell is in the selected slice
      const isInSelectedSlice = isCellInSelectedSlice(xIndex, yIndex, zIndex);

      // If in cell detail mode and clicking an active slice cell, emit cell-click
      if (props.cellDetailMode && isInSelectedSlice) {
        const stakeholder = kdm.value?.stake_holders?.[zIndex];
        const row = kdm.value?.rows?.[yIndex];
        const col = kdm.value?.columns?.[xIndex];
        if (stakeholder && row && col) {
          const cellId = `${stakeholder.id}-${row.id}-${col.id}`;
          emit('cell-click', cellId, zIndex, yIndex, xIndex);
          return;
        }
      }

      // Otherwise, select the slice as before
      let index: number;
      if (selectedAxis === 'x') {
        index = xIndex;
      } else if (selectedAxis === 'y') {
        index = yIndex;
      } else {
        index = zIndex;
      }
      emit('select-slice', selectedAxis, index);
      return;
    }
  }
}

function onMouseMove(event: MouseEvent) {
  if (!containerRef.value || !props.cellDetailMode) {
    if (hoveredCell.value) {
      hoveredCell.value = null;
      if (containerRef.value) containerRef.value.style.cursor = 'grab';
    }
    return;
  }

  const rect = containerRef.value.getBoundingClientRect();
  mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
  mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

  raycaster.setFromCamera(mouse, camera);

  // Check for hovering over cells in the active slice
  const cellIntersects = raycaster.intersectObjects(cubeGroup.children, true);
  let foundHoverableCell = false;

  for (const intersect of cellIntersects) {
    const obj = intersect.object;
    if (obj.userData.type === 'cell') {
      const { xIndex, yIndex, zIndex } = obj.userData;
      const isInSelectedSlice = isCellInSelectedSlice(xIndex, yIndex, zIndex);

      if (isInSelectedSlice) {
        hoveredCell.value = obj as THREE.Mesh;
        containerRef.value.style.cursor = 'pointer';
        foundHoverableCell = true;
        break;
      }
    }
  }

  if (!foundHoverableCell) {
    hoveredCell.value = null;
    containerRef.value.style.cursor = 'grab';
  }
}

function animate() {
  animationId = requestAnimationFrame(animate);
  controls.update();
  renderer.render(scene, camera);
}

function onResize() {
  if (!containerRef.value) return;

  const width = containerRef.value.clientWidth;
  const height = containerRef.value.clientHeight;

  camera.aspect = width / height;
  camera.updateProjectionMatrix();
  renderer.setSize(width, height);
}

function init(retryCount = 0) {
  if (!containerRef.value) {
    console.warn('KDMCube3D: containerRef not available');
    return;
  }

  const width = containerRef.value.clientWidth;
  const height = containerRef.value.clientHeight;

  // If container has no size yet, retry after a short delay (max 5 retries)
  if ((width === 0 || height === 0) && retryCount < 5) {
    console.warn(`KDMCube3D: container size is ${width}x${height}, retrying... (${retryCount + 1}/5)`);
    setTimeout(() => init(retryCount + 1), 100);
    return;
  }

  if (width === 0 || height === 0) {
    console.error('KDMCube3D: container has no size after retries');
    return;
  }

  scene = new THREE.Scene();
  scene.background = new THREE.Color(0xf8f9fa);

  camera = new THREE.PerspectiveCamera(50, width / height, 0.1, 1000);
  camera.position.set(8, 6, 12);

  renderer = new THREE.WebGLRenderer({ antialias: true });
  renderer.setSize(width, height);
  renderer.setPixelRatio(window.devicePixelRatio);
  containerRef.value.appendChild(renderer.domElement);

  controls = new OrbitControls(camera, renderer.domElement);
  controls.enableDamping = true;
  controls.dampingFactor = 0.05;
  controls.minDistance = 5;
  controls.maxDistance = 40;

  const ambientLight = new THREE.AmbientLight(0xffffff, 0.6);
  scene.add(ambientLight);

  const directionalLight = new THREE.DirectionalLight(0xffffff, 0.8);
  directionalLight.position.set(10, 10, 10);
  scene.add(directionalLight);

  const directionalLight2 = new THREE.DirectionalLight(0xffffff, 0.4);
  directionalLight2.position.set(-10, -10, -10);
  scene.add(directionalLight2);

  raycaster = new THREE.Raycaster();
  mouse = new THREE.Vector2();

  containerRef.value.addEventListener('click', onMouseClick);
  containerRef.value.addEventListener('mousemove', onMouseMove);
  window.addEventListener('resize', onResize);

  // Use ResizeObserver to detect container size changes (e.g., when drawer opens/closes)
  resizeObserver = new ResizeObserver(() => {
    onResize();
  });
  resizeObserver.observe(containerRef.value);

  createCube();
  createAxesHelper();
  animate();
}

onMounted(() => {
  init();
});

onUnmounted(() => {
  if (animationId) cancelAnimationFrame(animationId);
  if (containerRef.value) {
    containerRef.value.removeEventListener('click', onMouseClick);
    containerRef.value.removeEventListener('mousemove', onMouseMove);
  }
  window.removeEventListener('resize', onResize);
  if (resizeObserver) {
    resizeObserver.disconnect();
    resizeObserver = null;
  }
  if (renderer) renderer.dispose();
});

// Watch for selection changes (both axis and index)
watch([() => props.selectedAxis, () => props.selectedIndex], () => {
  createCube(); // Recreate labels with updated colors
  createAxesHelper(); // Recreate axes with updated thickness
});

watch(() => kdm.value?.field_matrix_data, () => {
  createCube();
  createAxesHelper();
});

// Watch for axis changes (add/delete rows, columns, stakeholders)
watch(
  [
    () => kdm.value?.rows?.length,
    () => kdm.value?.columns?.length,
    () => kdm.value?.stake_holders?.length,
  ],
  () => {
    createCube();
    createAxesHelper();
  }
);

// Create coordinate axes with arrows - active axis is thicker and darker
function createAxesHelper() {
  if (!scene) return;

  // Remove existing axes
  if (axesGroup) {
    scene.remove(axesGroup);
    axesGroup = null;
  }

  if (!props.showAxes) return;

  const rows = kdm.value?.rows || [];
  const cols = kdm.value?.columns || [];
  const stakeholders = kdm.value?.stake_holders || [];

  // Fixed cube structure: X=columns, Y=rows, Z=stakeholders
  const totalWidth = cols.length * (CELL_SIZE + CELL_GAP);
  const totalHeight = rows.length * (CELL_SIZE + CELL_GAP);
  const totalDepth = stakeholders.length * (CELL_SIZE + LAYER_GAP);

  axesGroup = new THREE.Group();

  // Axes lengths based on cube dimensions + margin
  const xAxisLength = totalWidth + 2;
  const yAxisLength = totalHeight + 2;
  const zAxisLength = totalDepth + 2;

  const { selectedAxis } = props;

  // Helper to create axis cylinder (thicker than line)
  function createAxisCylinder(length: number, color: number, isActive: boolean): THREE.Mesh {
    const radius = isActive ? 0.06 : 0.025;
    const geometry = new THREE.CylinderGeometry(radius, radius, length, 8);
    const material = new THREE.MeshBasicMaterial({ color });
    const mesh = new THREE.Mesh(geometry, material);
    return mesh;
  }

  // X Axis (Red) - along columns
  const xIsActive = selectedAxis === 'x';
  const xColor = xIsActive ? 0xcc0000 : 0xff6666;
  const xArrowRadius = xIsActive ? 0.18 : 0.1;
  const xArrowLength = xIsActive ? 0.5 : 0.35;

  const xCylinder = createAxisCylinder(xAxisLength, xColor, xIsActive);
  xCylinder.rotation.z = -Math.PI / 2;
  xCylinder.position.set(xAxisLength / 2, 0, 0);
  axesGroup.add(xCylinder);

  const xArrowGeometry = new THREE.ConeGeometry(xArrowRadius, xArrowLength, 8);
  const xArrowMaterial = new THREE.MeshBasicMaterial({ color: xColor });
  const xArrow = new THREE.Mesh(xArrowGeometry, xArrowMaterial);
  xArrow.position.set(xAxisLength + xArrowLength / 2, 0, 0);
  xArrow.rotation.z = -Math.PI / 2;
  axesGroup.add(xArrow);

  const xLabel = createTextSprite('X', { fontSize: xIsActive ? 56 : 48, color: xIsActive ? '#990000' : '#ff6666' });
  xLabel.position.set(xAxisLength + 0.9, 0, 0);
  xLabel.scale.set(xIsActive ? 1 : 0.8, xIsActive ? 0.25 : 0.2, 1);
  axesGroup.add(xLabel);

  // Y Axis (Green) - along rows (going up)
  const yIsActive = selectedAxis === 'y';
  const yColor = yIsActive ? 0x008800 : 0x66cc66;
  const yArrowRadius = yIsActive ? 0.18 : 0.1;
  const yArrowLength = yIsActive ? 0.5 : 0.35;

  const yCylinder = createAxisCylinder(yAxisLength, yColor, yIsActive);
  yCylinder.position.set(0, yAxisLength / 2, 0);
  axesGroup.add(yCylinder);

  const yArrowGeometry = new THREE.ConeGeometry(yArrowRadius, yArrowLength, 8);
  const yArrowMaterial = new THREE.MeshBasicMaterial({ color: yColor });
  const yArrow = new THREE.Mesh(yArrowGeometry, yArrowMaterial);
  yArrow.position.set(0, yAxisLength + yArrowLength / 2, 0);
  axesGroup.add(yArrow);

  const yLabel = createTextSprite('Y', { fontSize: yIsActive ? 56 : 48, color: yIsActive ? '#006600' : '#66cc66' });
  yLabel.position.set(0, yAxisLength + 0.9, 0);
  yLabel.scale.set(yIsActive ? 1 : 0.8, yIsActive ? 0.25 : 0.2, 1);
  axesGroup.add(yLabel);

  // Z Axis (Blue) - along stakeholders (depth, going backward/negative Z)
  const zIsActive = selectedAxis === 'z';
  const zColor = zIsActive ? 0x0000cc : 0x6666ff;
  const zArrowRadius = zIsActive ? 0.18 : 0.1;
  const zArrowLength = zIsActive ? 0.5 : 0.35;

  const zCylinder = createAxisCylinder(zAxisLength, zColor, zIsActive);
  zCylinder.rotation.x = Math.PI / 2;
  zCylinder.position.set(0, 0, -zAxisLength / 2);
  axesGroup.add(zCylinder);

  const zArrowGeometry = new THREE.ConeGeometry(zArrowRadius, zArrowLength, 8);
  const zArrowMaterial = new THREE.MeshBasicMaterial({ color: zColor });
  const zArrow = new THREE.Mesh(zArrowGeometry, zArrowMaterial);
  zArrow.position.set(0, 0, -zAxisLength - zArrowLength / 2);
  zArrow.rotation.x = -Math.PI / 2;
  axesGroup.add(zArrow);

  const zLabel = createTextSprite('Z', { fontSize: zIsActive ? 56 : 48, color: zIsActive ? '#000099' : '#6666ff' });
  zLabel.position.set(0, 0, -zAxisLength - 0.9);
  zLabel.scale.set(zIsActive ? 1 : 0.8, zIsActive ? 0.25 : 0.2, 1);
  axesGroup.add(zLabel);

  // Position axes at the bottom-left-front corner of the cube
  // The cube is centered, so we offset to align with its corner
  axesGroup.position.set(
    -totalWidth / 2 - 0.5,           // Left edge of cube
    -totalHeight / 2 - 0.5,          // Bottom edge of cube
    totalDepth / 2 + 0.5             // Front edge of cube (first stakeholder layer)
  );

  scene.add(axesGroup);
}

// Watch for axes toggle
watch(() => props.showAxes, () => {
  createAxesHelper();
});

// Watch for labels toggle
watch(() => props.showLabels, (show) => {
  if (labelsGroup) {
    labelsGroup.visible = show ?? true;
  }
});

// Watch for full labels toggle - recreate cube to update labels
watch(() => props.showFullLabels, () => {
  createCube();
  createAxesHelper();
});


// Watch for evaluation values filter
watch(() => props.visibleEvaluationValues, () => {
  createCube();
}, { deep: true });

// Watch for opacity toggle
watch(() => props.showAllOpaque, () => {
  updateLayerOpacity();
});

// Watch for selected cell change to highlight it
watch(() => props.selectedCellId, () => {
  updateSelectedCellHighlight();
});

// Watch for cell detail mode toggle
watch(() => props.cellDetailMode, (newVal) => {
  if (!newVal && containerRef.value) {
    containerRef.value.style.cursor = 'grab';
  }
  updateSelectedCellHighlight();
});

let selectedCellHighlight: THREE.LineSegments | null = null;

function updateSelectedCellHighlight() {
  // Remove existing highlight
  if (selectedCellHighlight && scene) {
    scene.remove(selectedCellHighlight);
    selectedCellHighlight.geometry.dispose();
    (selectedCellHighlight.material as THREE.Material).dispose();
    selectedCellHighlight = null;
  }

  if (!props.cellDetailMode || !props.selectedCellId || !cubeGroup || !kdm.value) return;

  // Parse the cell ID to find indices
  const parts = props.selectedCellId.split('-');
  if (parts.length !== 3) return;

  const [stakeholderId, rowId, colId] = parts;
  const stakeholders = kdm.value.stake_holders || [];
  const rows = kdm.value.rows || [];
  const cols = kdm.value.columns || [];

  const zIndex = stakeholders.findIndex(sh => sh.id === stakeholderId);
  const yIndex = rows.findIndex(r => r.id === rowId);
  const xIndex = cols.findIndex(c => c.id === colId);

  if (zIndex < 0 || yIndex < 0 || xIndex < 0) return;

  // Find the cell mesh and create highlight
  const totalWidth = cols.length * (CELL_SIZE + CELL_GAP);
  const totalHeight = rows.length * (CELL_SIZE + CELL_GAP);
  const totalDepth = stakeholders.length * (CELL_SIZE + LAYER_GAP);

  const cellX = xIndex * (CELL_SIZE + CELL_GAP) - totalWidth / 2 + CELL_SIZE / 2;
  const cellY = -yIndex * (CELL_SIZE + CELL_GAP) + totalHeight / 2 - CELL_SIZE / 2;
  const cellZ = -zIndex * (CELL_SIZE + LAYER_GAP) + totalDepth / 2;

  // Create highlight border
  const highlightGeometry = new THREE.BoxGeometry(CELL_SIZE + 0.1, CELL_SIZE + 0.1, CELL_SIZE * 0.3 + 0.1);
  const edges = new THREE.EdgesGeometry(highlightGeometry);
  const highlightMaterial = new THREE.LineBasicMaterial({
    color: 0x0d6efd, // Bootstrap primary blue
    linewidth: 2
  });

  selectedCellHighlight = new THREE.LineSegments(edges, highlightMaterial);
  selectedCellHighlight.position.set(cellX, cellY, cellZ);
  scene.add(selectedCellHighlight);
}
</script>

<template>
  <div ref="containerRef" class="kdm-cube-3d" :class="{ 'kdm-cube-3d--fullscreen': isFullscreen }"></div>
</template>

<style scoped>
.kdm-cube-3d {
  width: 100%;
  height: 500px;
  border-radius: 8px;
  overflow: hidden;
  cursor: grab;
}

.kdm-cube-3d--fullscreen {
  flex: 1 1 auto;
  min-height: 300px;
  height: 100%;
}

.kdm-cube-3d:active {
  cursor: grabbing;
}
</style>
